import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';

interface DeckVotes {
  upvotes: number;
  downvotes: number;
  userVote: boolean | null;
}

export function useDeckVotes(deckId: string) {
  const { user } = useAuth();
  const [votes, setVotes] = useState<DeckVotes>({ upvotes: 0, downvotes: 0, userVote: null });
  const [loading, setLoading] = useState(true);

  const fetchVotes = async () => {
    try {
      // Get all votes for the deck
      const { data: voteData } = await supabase
        .from('deck_votes')
        .select('vote')
        .eq('deck_id', deckId);

      // Get user's vote if authenticated
      let userVote = null;
      if (user) {
        const { data: userVoteData } = await supabase
          .from('deck_votes')
          .select('vote')
          .eq('deck_id', deckId)
          .eq('user_id', user.id);
        
        if (userVoteData && userVoteData.length > 0) {
          userVote = userVoteData[0].vote;
        }
      }

      if (voteData) {
        const upvotes = voteData.filter(v => v.vote).length;
        const downvotes = voteData.filter(v => !v.vote).length;
        setVotes({
          upvotes,
          downvotes,
          userVote
        });
      }
    } catch (error) {
      console.error('Error fetching votes:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVotes();
  }, [deckId, user?.id]);

  const vote = async (isUpvote: boolean) => {
    if (!user) return;

    try {
      const { data: existingVotes } = await supabase
        .from('deck_votes')
        .select('*')
        .eq('deck_id', deckId)
        .eq('user_id', user.id);

      if (existingVotes && existingVotes.length > 0) {
        const existingVote = existingVotes[0];
        if (existingVote.vote === isUpvote) {
          // Remove vote if clicking the same button
          await supabase
            .from('deck_votes')
            .delete()
            .eq('deck_id', deckId)
            .eq('user_id', user.id);
        } else {
          // Update vote
          await supabase
            .from('deck_votes')
            .update({ vote: isUpvote })
            .eq('deck_id', deckId)
            .eq('user_id', user.id);
        }
      } else {
        // Insert new vote
        await supabase
          .from('deck_votes')
          .insert({
            deck_id: deckId,
            user_id: user.id,
            vote: isUpvote
          });
      }

      await fetchVotes();
    } catch (error) {
      console.error('Error voting:', error);
    }
  };

  return {
    votes,
    loading,
    vote
  };
}